import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { FormInput } from "tsi-common-react/src/forms/FormInput/index";
import { FormPhoneNumber } from "tsi-common-react/src/forms/FormPhoneNumber/index";
import { RetailStore } from "tsi-common-react/src/models/location";
import {
    SyncStoreID,
    isoSyncStoreID,
} from "tsi-common-react/src/models/nominals";
import { isEqualSet } from "tsi-common-react/src/utils/sets";
import { t } from "ttag";

import { StoreRowLocation } from "../LocatorNearestStoresRow";

// 'Email Your Results'
// Check up to <strong>10</strong> <span>results</span> you would like emailed.

const getFirstResultIDs = (
    stores: Immutable<RetailStore[]>,
    maxSelected: number,
): Set<SyncStoreID> => {
    return new Set(
        stores.slice(0, maxSelected).map((s) => {
            return s.external_id;
        }),
    );
};

const firstResultsAreSelected = (
    stores: Immutable<RetailStore[]>,
    maxSelected: number,
    selected: Set<SyncStoreID>,
): boolean => {
    const firstResultIDs = getFirstResultIDs(stores, maxSelected);
    return isEqualSet(firstResultIDs, selected);
};

const buildInput = (
    inputType: "email" | "phone" | undefined,
    inputPlaceholder: string | undefined,
    recipient: string,
    isSubmitting: boolean,
    onRecipientChange: (e: React.FormEvent<HTMLInputElement>) => void,
): React.ReactNode => {
    if (!inputType) {
        return null;
    }
    if (inputType === "phone") {
        return (
            <FormPhoneNumber
                required={true}
                label={t`Phone Number`}
                placeholder={inputPlaceholder}
                value={recipient}
                onChange={onRecipientChange}
                disabled={isSubmitting}
                id={inputType}
                name={inputType}
            />
        );
    }
    return (
        <FormInput
            required={true}
            label={t`Email Address`}
            placeholder={inputPlaceholder}
            type={inputType}
            value={recipient}
            onChange={onRecipientChange}
            disabled={isSubmitting}
            id={inputType}
            name={inputType}
        />
    );
};

const buildStoreRow = (
    store: Immutable<RetailStore>,
    selected: Set<SyncStoreID>,
    maxSelected: number,
    isSubmitting: boolean,
    getStoreNumber: (store: Immutable<RetailStore>) => number,
    onSelectChange: (e: React.FormEvent<HTMLInputElement>) => void,
): React.ReactNode => {
    const isChecked = selected.has(store.external_id);
    const uniqueLocationID = "loc" + "-" + store.external_id.toString();
    const rowClasses = classNames({
        selected: isChecked,
    });
    return (
        <li key={`${store.external_id}`} className={rowClasses}>
            <label htmlFor={uniqueLocationID}>
                <div className="loc__checkbox-wrapper">
                    <input
                        className="loc__checkbox"
                        type="checkbox"
                        value={isoSyncStoreID.unwrap(store.external_id)}
                        checked={isChecked}
                        disabled={
                            (!isChecked && selected.size >= maxSelected) ||
                            isSubmitting
                        }
                        onChange={onSelectChange}
                        id={uniqueLocationID}
                    />
                </div>
                <StoreRowLocation
                    store={store}
                    showWebsite={false}
                    getStoreNumber={getStoreNumber}
                    labelByStoreName={true}
                />
            </label>
        </li>
    );
};

export const LocatorSendResults = (props: {
    title: string;
    description: string | React.ReactNode;
    inputType?: "email" | "phone";
    inputPlaceholder?: string;
    submitButtonLabel: string;
    maxSelected: number;
    stores: Immutable<RetailStore[]>;
    isSubmitting: boolean;
    getStoreNumber: (store: Immutable<RetailStore>) => number;
    onCancel: () => void;
    onSubmit: (recipient: string, selectedStoreIDs: Set<SyncStoreID>) => void;
}): React.ReactNode => {
    const [recipient, setRecipient] = React.useState("");
    const [selected, setSelected] = React.useState<Set<SyncStoreID>>(new Set());

    const onRecipientChange = (e: React.FormEvent<HTMLInputElement>) => {
        setRecipient(e.currentTarget.value);
    };

    const onSelectChange = (e: React.FormEvent<HTMLInputElement>) => {
        const storeID = isoSyncStoreID.wrap(
            parseInt(e.currentTarget.value, 10),
        );
        const checked = e.currentTarget.checked;
        setSelected((prevSelected) => {
            const newSelected = new Set(prevSelected);
            if (checked && newSelected.size < props.maxSelected) {
                newSelected.add(storeID);
            } else {
                newSelected.delete(storeID);
            }
            return newSelected;
        });
    };

    const onSelectFirstResults = (e: React.FormEvent<HTMLInputElement>) => {
        setSelected(
            e.currentTarget.checked
                ? getFirstResultIDs(props.stores, props.maxSelected)
                : new Set(),
        );
    };

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        props.onSubmit(recipient, selected);
    };

    return (
        <div className="send__content survey__carousel">
            <h1>{props.title}</h1>
            <form onSubmit={onSubmit}>
                <fieldset>
                    <legend className="ada-screenreader-only">
                        {props.title}
                    </legend>
                    {props.inputType && (
                        <>
                            <p
                                className="send__required-legend"
                                aria-hidden="true"
                            >
                                {t`* indicates a required field`}
                            </p>
                            <div className="send__inputs">
                                {buildInput(
                                    props.inputType,
                                    props.inputPlaceholder,
                                    recipient,
                                    props.isSubmitting,
                                    onRecipientChange,
                                )}
                            </div>
                        </>
                    )}
                    <fieldset className="send__results">
                        <legend className="survey__note">
                            {props.description}
                        </legend>
                        <label>
                            <input
                                type="checkbox"
                                checked={firstResultsAreSelected(
                                    props.stores,
                                    props.maxSelected,
                                    selected,
                                )}
                                onChange={onSelectFirstResults}
                            />
                            <span>{t`Check First ${props.maxSelected} Results`}</span>
                        </label>
                        <ul className="send__list">
                            {props.stores.map((store) => {
                                return buildStoreRow(
                                    store,
                                    selected,
                                    props.maxSelected,
                                    props.isSubmitting,
                                    props.getStoreNumber,
                                    onSelectChange,
                                );
                            })}
                        </ul>
                    </fieldset>
                    <div className="send__actions">
                        <button
                            className="button send__button retail-locator__share-button--text al-store-locator__share-button--text"
                            type="submit"
                            aria-label={props.submitButtonLabel}
                            disabled={selected.size <= 0 || props.isSubmitting}
                        >
                            {props.submitButtonLabel}
                        </button>
                        <a
                            title={t`Close`}
                            className="send__cancel modal-cancel"
                            onClick={props.onCancel}
                            href="#"
                        >
                            {t`Cancel`}
                        </a>
                    </div>
                </fieldset>
            </form>
        </div>
    );
};
